/**
 * Destaque do termo pesquisado nos resultados da busca.
 *
 * O casamento repete o que `buildContentSearchRegex` faz na API: ignora acento
 * e caixa e aceita espaço extra digitado. Aqui o texto já chega sem HTML — o
 * card exibe o `textContent` do enunciado —, então não é preciso tratar tag
 * nem entidade.
 */

const MAX_TERM_LENGTH = 200;

/** Letras equivalentes para efeito de busca, como no filtro da API. */
const DIACRITIC_GROUPS = ['aáàâãäå', 'cç', 'eéèêë', 'iíìîï', 'nñ', 'oóòôõö', 'uúùûü', 'yýÿ'];

/** Entre duas palavras do termo: espaço ou espaço fixo. */
const WORD_SEPARATOR = '[\\s\\u00a0]+';

const REGEX_SPECIAL = /[.*+?^${}()|[\]\\/-]/g;

const escapeRegex = (value: string) => value.replace(REGEX_SPECIAL, '\\$&');

const CHAR_PATTERNS = DIACRITIC_GROUPS.reduce((map, group) => {
  const chars = Array.from(new Set(Array.from(group + group.toUpperCase()))).join('');

  for (const char of group) {
    map.set(char, `[${chars}]`);
    map.set(char.toUpperCase(), `[${chars}]`);
  }

  return map;
}, new Map<string, string>());

const buildWordPattern = (word: string) =>
  Array.from(word)
    .map((char) => CHAR_PATTERNS.get(char) || escapeRegex(char))
    .join('');

export interface IHighlightPart {
  text: string;
  match: boolean;
}

/** Expressão do destaque, ou `undefined` quando não há termo utilizável. */
export function buildHighlightRegex(term: unknown): RegExp | undefined {
  if (typeof term !== 'string') return undefined;

  const words = term
    .slice(0, MAX_TERM_LENGTH)
    .trim()
    .split(/[\s]+/)
    .filter(Boolean);

  if (!words.length) return undefined;

  return new RegExp(words.map(buildWordPattern).join(WORD_SEPARATOR), 'gi');
}

const findMatches = (text: string, regex: RegExp) => {
  const matches: [number, number][] = [];
  let match = regex.exec(text);

  while (match !== null) {
    if (match[0] === '') {
      regex.lastIndex += 1;
    } else {
      matches.push([match.index, match.index + match[0].length]);
    }
    match = regex.exec(text);
  }

  return matches;
};

/** Quebra o texto nos trechos que casam com o termo. */
export function splitByMatches(text: string, term: unknown): IHighlightPart[] {
  if (!text) return [];

  const regex = buildHighlightRegex(term);
  if (!regex) return [{ text, match: false }];

  const parts: IHighlightPart[] = [];
  let cursor = 0;

  findMatches(text, regex).forEach(([start, end]) => {
    if (start > cursor) parts.push({ text: text.slice(cursor, start), match: false });
    parts.push({ text: text.slice(start, end), match: true });
    cursor = end;
  });

  if (cursor < text.length) parts.push({ text: text.slice(cursor), match: false });

  return parts;
}

/**
 * Até onde o primeiro destaque pode ficar do começo antes de o texto ser
 * recortado. O card mostra só duas linhas, então um termo no meio do
 * enunciado sumia atrás das reticências do `line-clamp`.
 */
const MAX_LEADING_CHARS = 60;

/** Quanto de contexto manter antes do destaque, quando o texto é recortado. */
const CONTEXT_CHARS = 30;

/**
 * Mesma quebra, mas começando perto do primeiro trecho encontrado quando ele
 * está longe do início. O corte acontece num espaço, para não partir palavra.
 */
export function splitSnippetByMatches(text: string, term: unknown): IHighlightPart[] {
  const regex = buildHighlightRegex(term);
  const first = regex ? regex.exec(text) : null;

  if (!first || first.index <= MAX_LEADING_CHARS) return splitByMatches(text, term);

  const space = text.lastIndexOf(' ', first.index - CONTEXT_CHARS);
  const start = space > 0 ? space + 1 : first.index - CONTEXT_CHARS;

  return splitByMatches(`…${text.slice(start)}`, term);
}
